import { createContext, useContext, useMemo, useState } from "react";
import axios from "axios";

import { API_URL } from "./config";
import Service from "../../api/service";
import { useAdminContext } from "../admin/AdminContext";

/**
 * One factory for every "resource" context in the codebase.
 *
 * The shape:
 *   const { Provider, useValue } = createCrudContext({
 *     name: "climate",             // just a label for error messages
 *     listPath: "climate",         // GET  /api/v1/<listPath>/
 *     adminPath: "climate",        // POST /api/v1/<adminPrefix><adminPath>/create (and update/delete)
 *     adminPrefix: "admin/",       // set to "" when the API doesn't namespace admin routes (blog, advertisement)
 *     fetchOne: false,             // when true, exposes fetchOne(id) → GET /api/v1/<listPath>/<id>
 *     omit: [],                    // subset of ["fetch","create","update","remove"] to not generate
 *     extras: null | (tools) => {}, // callback for non-CRUD endpoints (sendNotification, singleRegionStats, …)
 *     legacy: { data, count, fetch, fetchOne, create, update, remove, updateState }, // alias map
 *   });
 *
 * Paths can be strings or `(sysAdmin) => string` for URLs that depend
 * on the current admin (e.g. payment scopes to company_id).
 *
 * `extras` receives { sysAdmin, data, setData, count, setCount, request, authConfig }
 * where `request` is { get, post, put, del } — all auto-auth'd and error-safe.
 */
type PathResolver = string | ((sysAdmin: any) => string);
type LegacyAliases = Partial<{
  data: string;
  count: string;
  selected: string;
  setSelected: string;
  fetch: string;
  fetchOne: string;
  create: string;
  update: string;
  remove: string;
  updateState: string;
}>;

type CrudSpec = {
  name: string;
  listPath?: PathResolver;
  adminPath?: PathResolver;
  adminPrefix?: string;
  fetchOne?: boolean;
  omit?: string[];
  extras?: ((tools: any) => Record<string, any>) | null;
  legacy?: LegacyAliases;
  // "axios" → raw axios + response.data.data.{data,count}
  // "service" → app Service client + response.data.{data,count}
  client?: "axios" | "service";
};

export const createCrudContext = ({
  name,
  listPath,
  adminPath,
  adminPrefix = "admin/",
  fetchOne = false,
  omit = [],
  extras = null,
  legacy = {},
  client = "axios",
}: CrudSpec) => {
  const resolvePath = (value: PathResolver | undefined, sysAdmin: any) => {
    if (typeof value === "function") return value(sysAdmin);
    return value ?? name;
  };

  const Ctx = createContext(null);

  const Provider = ({ children }) => {
    const [data, setData] = useState(null);
    const [count, setCount] = useState(0);
    const [selected, setSelected] = useState(null);
    const { sysAdmin } = useAdminContext();

    const authConfig = () => ({
      headers: { Authorization: `Bearer ${sysAdmin?.token}` },
    });

    const safeCall = async (promise) => {
      let errorResponse = null;
      const response = await promise.catch((error) => {
        errorResponse = error?.response ?? null;
      });
      return response ?? errorResponse;
    };

    // Request adapter. `axios` → returns raw axios response; list data lives
    // at response.data.data.{data,count}. `service` → the app's Service client
    // auto-adds auth, pre-unwraps to {data, message, status}; list data lives
    // at response.data.{data,count}.
    const request =
      client === "service"
        ? {
            get: (url) => Service.get(url),
            post: (url, body) => Service.post(url, body),
            put: (url, body) => Service.put(url, body),
            del: (url) => Service.delete(url),
          }
        : {
            // Auth is attached to GETs too — the pre-factory contexts did this,
            // and the shared 401 interceptor in api/http.js force-logs-out on
            // any unauthenticated GET against a protected endpoint.
            get: (url) => safeCall(axios.get(url, authConfig())),
            post: (url, body) => safeCall(axios.post(url, body, authConfig())),
            put: (url, body) => safeCall(axios.put(url, body, authConfig())),
            del: (url) => safeCall(axios.delete(url, authConfig())),
          };

    const readListPayload = (response) =>
      client === "service"
        ? { data: response?.data?.data, count: response?.data?.count }
        : {
            data: response?.data?.data?.data,
            count: response?.data?.data?.count,
          };

    const readOnePayload = (response) =>
      client === "service" ? response?.data : response?.data?.data;

    const url = {
      list: (page = 1, pageSize = 50) =>
        `${API_URL}/api/v1/${resolvePath(listPath, sysAdmin)}/?page=${page}&pageSize=${pageSize}`,
      one: (id) =>
        `${API_URL}/api/v1/${resolvePath(listPath, sysAdmin)}/${id}`,
      admin: (suffix) =>
        `${API_URL}/api/v1/${adminPrefix}${resolvePath(adminPath, sysAdmin)}/${suffix}`,
    };

    const fetchAll = omit.includes("fetch")
      ? undefined
      : async (page = 1, pageSize = 50) => {
          const response = await request.get(url.list(page, pageSize));
          const payload = readListPayload(response);
          if (payload.data !== undefined) {
            setData(payload.data);
            if (payload.count !== undefined) setCount(payload.count);
          }
          return response;
        };

    const fetchOneMethod = fetchOne
      ? async (id) => {
          const response = await request.get(url.one(id));
          const one = readOnePayload(response);
          if (one !== undefined) setSelected(one);
          return response;
        }
      : undefined;

    const createOne = omit.includes("create")
      ? undefined
      : async (payload) => request.post(url.admin("create"), payload);

    const updateOne = omit.includes("update")
      ? undefined
      : async (id, payload) => request.put(url.admin(`update/${id}`), payload);

    const deleteOne = omit.includes("remove")
      ? undefined
      : async (id) => request.del(url.admin(`delete/${id}`));

    const updateState = async (next) => setData(next);

    const extraMethods = extras
      ? extras({
          sysAdmin,
          data,
          setData,
          count,
          setCount,
          selected,
          setSelected,
          request,
          authConfig,
          API_URL,
        })
      : {};

    const value = useMemo(() => {
      const generic = {
        data,
        count,
        selected,
        setSelected,
        fetchAll,
        fetchOne: fetchOneMethod,
        createOne,
        updateOne,
        deleteOne,
        updateState,
      };

      const aliased = {};
      if (legacy.data) aliased[legacy.data] = data;
      if (legacy.count) aliased[legacy.count] = count;
      if (legacy.selected) aliased[legacy.selected] = selected;
      if (legacy.setSelected) aliased[legacy.setSelected] = setSelected;
      if (legacy.fetch && fetchAll) aliased[legacy.fetch] = fetchAll;
      if (legacy.fetchOne && fetchOneMethod)
        aliased[legacy.fetchOne] = fetchOneMethod;
      if (legacy.create && createOne) aliased[legacy.create] = createOne;
      if (legacy.update && updateOne) aliased[legacy.update] = updateOne;
      if (legacy.remove && deleteOne) aliased[legacy.remove] = deleteOne;
      if (legacy.updateState) aliased[legacy.updateState] = updateState;

      return { ...generic, ...aliased, ...extraMethods };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, count, selected, sysAdmin?.token]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  };

  const useValue = () => {
    const v = useContext(Ctx);
    if (!v) {
      throw new Error(
        `use${name[0].toUpperCase()}${name.slice(1)}Context must be used inside its Provider`,
      );
    }
    return v;
  };

  return { Context: Ctx, Provider, useValue };
};
